import { useNavigate, useLocation } from 'react-router-dom';
import { type ConnectionStatus } from '../types/gateway';
import { type UserRole } from '../types/gateway';
import type { SessionInfo } from '../types/gateway';
import { RoleSwitcher } from './RoleSwitcher';
import { UserBadge } from './UserBadge';
import { SessionMenu } from './SessionMenu';
import styles from './TopBar.module.css';

interface TopBarProps {
  connectionStatus: ConnectionStatus;
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
  sessions?: SessionInfo[];
  activeSessionId?: string | null;
  onCreateSession?: () => void;
  onSwitchSession?: (id: string) => void;
  onToggleSidebar?: () => void;
  /** When true, render the Admin / Users button next to the role switcher. */
  isAdmin?: boolean;
  /** Click handler for the Admin button. Required when `isAdmin` is true. */
  onOpenAdmin?: () => void;
  /** Click handler for the Feedback triage button. Admin-only. */
  onOpenFeedback?: () => void;
  /** Number of `open`-status feedback reports. Drives the badge on the Feedback button. */
  openFeedbackCount?: number;
  /** Click handler for the Settings button. Visible to all logged-in users. */
  onOpenSettings?: () => void;
  /** Print / Save-as-PDF the current conversation. Shown on the Chat tab. */
  onPrintChat?: () => void;
}

export function TopBar({
  connectionStatus,
  currentRole,
  onRoleChange,
  sessions = [],
  activeSessionId = null,
  onCreateSession,
  onSwitchSession,
  onToggleSidebar,
  isAdmin = false,
  onOpenAdmin,
  onOpenFeedback,
  openFeedbackCount = 0,
  onOpenSettings,
  onPrintChat,
}: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isChat = location.pathname === '/';
  const isTraining = location.pathname === '/training';
  const isHelp = location.pathname === '/help';

  const statusColor =
    connectionStatus === 'connected'
      ? 'var(--green)'
      : connectionStatus === 'connecting'
        ? 'var(--amber)'
        : 'var(--red)';

  const statusText =
    connectionStatus === 'connected'
      ? 'Gateway Connected'
      : connectionStatus === 'connecting'
        ? 'Connecting...'
        : 'Disconnected';

  return (
    <header className={styles.topBar}>
      {onToggleSidebar && (
        <button
          className={styles.hamburger}
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
        >
          &#9776;
        </button>
      )}

      <div className={styles.brand}>
        <span className={styles.brandName}>HOLLER</span>
        <span className={styles.brandSub}>powered by <strong>FOREMAN</strong></span>
      </div>

      <nav className={styles.navGroup} aria-label="Primary">
        <button
          type="button"
          className={`${styles.navLink} ${isChat ? styles.navLinkActive : ''}`}
          onClick={() => navigate('/')}
        >
          Chat
        </button>
        <button
          type="button"
          className={`${styles.navLink} ${isTraining ? styles.navLinkActive : ''}`}
          onClick={() => navigate('/training')}
        >
          Training
        </button>
        <button
          type="button"
          className={`${styles.navLink} ${isHelp ? styles.navLinkActive : ''}`}
          onClick={() => navigate('/help')}
        >
          Help
        </button>
      </nav>

      {isChat && onCreateSession && onSwitchSession && (
        <SessionMenu
          sessions={sessions}
          activeSessionId={activeSessionId}
          onCreateSession={onCreateSession}
          onSwitchSession={onSwitchSession}
        />
      )}

      <div className={styles.status}>
        <span className={styles.statusDot} style={{ background: statusColor }} />
        <span className={styles.statusText}>{statusText}</span>
      </div>

      <RoleSwitcher currentRole={currentRole} onRoleChange={onRoleChange} />
      {isAdmin && onOpenAdmin && (
        <button
          type="button"
          className={`${styles.navLink} ${styles.iconLink}`}
          onClick={onOpenAdmin}
          title="User management"
          aria-label="User management"
        >
          <span aria-hidden>{'\u{1F465}'}</span>
          <span className={styles.optionalLabel}>Users</span>
        </button>
      )}
      {isAdmin && onOpenFeedback && (
        <button
          type="button"
          className={`${styles.navLink} ${styles.iconLink}`}
          onClick={onOpenFeedback}
          title={openFeedbackCount > 0
            ? `${openFeedbackCount} open feedback report${openFeedbackCount === 1 ? '' : 's'}`
            : 'Feedback queue'}
          aria-label="Feedback triage"
        >
          <span aria-hidden>{'\u{1F6A9}'}</span>
          <span className={styles.optionalLabel}>Feedback</span>
          {openFeedbackCount > 0 && (
            <span className={styles.badge} aria-hidden>
              {openFeedbackCount > 99 ? '99+' : openFeedbackCount}
            </span>
          )}
        </button>
      )}
      {isChat && onPrintChat && (
        <button
          type="button"
          className={`${styles.navLink} ${styles.iconLink}`}
          onClick={onPrintChat}
          title="Print or save this conversation as a PDF"
          aria-label="Print or save as PDF"
        >
          <span aria-hidden>{'\u{1F5A8}'}</span>
          <span className={styles.optionalLabel}>Print / PDF</span>
        </button>
      )}
      {onOpenSettings && (
        <button
          type="button"
          className={`${styles.navLink} ${styles.iconLink}`}
          onClick={onOpenSettings}
          title="Account settings"
          aria-label="Account settings"
        >
          <span aria-hidden>{'\u2699'}</span>
          <span className={styles.optionalLabel}>Settings</span>
        </button>
      )}
      <UserBadge />
    </header>
  );
}
